import { useMemo } from "react";
import { RootState, useSelector } from "src/store";

import { selectDevicesByGroupIds } from "@dashboard/devices/store/selectors/devices";
import { selectGroups } from "@dashboard/groups/store/selectors";
import { useLicenses } from "@dashboard/licenses/api/helpers";
import { OtaFile } from "@dashboard/rollouts/types";
import { useCurrentSubscription } from "@dashboard/workspaces/api/helpers";
import { Device } from "@devices/types";

import { buildUnifiedTree, GroupTreeNode } from "../RolloutUnifiedTree/RolloutUnifiedTree.utils";

const emptyGroupIds: number[] = [];

function getCompatibleDevices(devices: Device[], codename: string | undefined): Device[] {
    if (!codename) {
        return [];
    }
    return devices.filter((device) => device.product === codename);
}

function filterEmptyGroups(tree: GroupTreeNode[]): GroupTreeNode[] {
    return tree
        .filter((group) => group.totalDeviceCount > 0)
        .map((group) => ({
            ...group,
            children: filterEmptyGroups(group.children),
        }));
}

export function useUnifiedTree(otaFile: OtaFile | null) {
    const groups = useSelector(selectGroups);
    const { data: currentSubscription } = useCurrentSubscription({});
    const groupIds = currentSubscription?.groupIds ?? emptyGroupIds;
    const licenses = useLicenses();
    const devices = useSelector((state: RootState) => selectDevicesByGroupIds(state, groupIds));

    const compatibleDevices = useMemo(() => getCompatibleDevices(devices, otaFile?.codename), [devices, otaFile?.codename]);

    return useMemo(
        () => filterEmptyGroups(buildUnifiedTree(groups, licenses, compatibleDevices, otaFile?.compatibility)),
        [groups, licenses, compatibleDevices, otaFile?.compatibility],
    );
}
